// pastCanvasClient — 過去キャンバス一覧 + サムネ署名 URL の HTTP クライアント
// (past-canvas-new-document-2026-07-05.md §4.2 / §7 Slice 2)。
//
// dispatcher の 2 endpoint を叩く (両方 owner-gated = `Authorization: Bearer
// <admin_token>` 必須):
//   1. `GET /rooms/:roomId/past-canvases`
//        → `{ pastCanvases: [{ id, sessionId, snapshotSeq, createdAt, clearedAt,
//            layerCount }] }` (newest-first, 最大50件, URL 非同梱)。
//   2. `GET /rooms/:roomId/past-canvases/:pastCanvasId/snapshot-urls`
//        (`SNAPSHOT_PUBLISHER=s3` のときのみ mount、それ以外 404)
//        → `{ metaUrl, layerUrls: [{ id, url }], urlExpiresInSeconds }`。
//
// outcome union で返し、期待される失敗 (401/403/404/400/503/network) では throw
// しない (SnapshotLoader / strokeLogTimeline と同流儀)。呼び出し側は outcome で分岐。
// fetch は narrow な shape で注入可能 (= node vitest からスタブを渡せる)。

/**
 * Minimal `fetch` shape — SnapshotLoader `FetchFn` と同一。node テストでスタブを
 * 渡せるよう DOM `fetch` の全 overload は要求しない。
 */
export type FetchFn = (
  input: string,
  init?: { signal?: AbortSignal; headers?: Record<string, string>; method?: string; body?: string },
) => Promise<Response>;

const defaultFetchFn: FetchFn = (input, init) => {
  const f = globalThis.fetch as
    | ((url: string, init?: RequestInit) => Promise<Response>)
    | undefined;
  if (f === undefined) throw new Error('globalThis.fetch is not available');
  return f.call(globalThis, input, init as RequestInit | undefined);
};

// ── 一覧 (GET /rooms/:roomId/past-canvases) ─────────────────────────────────

/**
 * 何がこの行を作ったか (荒らし復旧 UI 第 1 段階、vandalism-recovery-ui-2026-09-27.md
 * §2 確定 4)。サーバ `PastCanvasKind` (server-only 型、web は独立に再定義する —
 * `PastCanvasListItem` 自体が既存の慣行として dispatcher レスポンスの DTO をここに
 * 再定義しているのと同じ理由) と同じ 3 値。`null` = 不明 (カラム追加以前の既存行 /
 * 判別不能な backfill 行)。
 */
export type PastCanvasKind = 'cleared' | 'restore-autosave' | 'picked' | 'excluded';

/**
 * `kind: 'excluded'` の行が「何を外して作られたか」(荒らし復旧 第 2 段階 ①、サーバ
 * `PastCanvasExclusion` と同形)。`excluded` 以外の行は `null`。
 */
export interface PastCanvasExclusionInfo {
  readonly targetSessionId: string;
  readonly targetPastCanvasId: string | null;
  readonly targetEndSeq: number;
  readonly baseSessionId: string | null;
  readonly baseSeq: number | null;
  readonly excludeUids: readonly string[];
  readonly excludeSids: readonly string[];
}

/** 一覧 1 件 (dispatcher レスポンスをそのまま。日時は ISO8601 文字列)。 */
export interface PastCanvasListItem {
  readonly id: string;
  readonly sessionId: string;
  /** その過去キャンバスの最終 snapshot 時点の seq (絵ログ共有の終端初期化に使う)。 */
  readonly snapshotSeq: number;
  /**
   * clear (rotate) 発生時点の `room.currentSeq` (入口統一、
   * vandalism-recovery-ui-2026-09-27.md §2「入口を『続きを描く』に統一」)。
   * `?recover=<seq>` が開く行を「`clearedSeq >= seq` を満たす最小の行」で決める
   * ための材料 (`pickRecoverTargetRow`)。DB の NULL (カラム追加以前の既存行) は
   * サーバ側で `0` にフォールバック済みなので常に `number`。
   */
  readonly clearedSeq: number;
  /** セッション開始 (= 前の全消し直後) の ISO8601。 */
  readonly createdAt: string;
  /** 全消しでこの過去キャンバスが確定した時刻の ISO8601 (一覧ラベルの主軸)。 */
  readonly clearedAt: string;
  /** 最終 snapshot のレイヤー枚数 (0 = snapshot 無し = サムネ不可)。 */
  readonly layerCount: number;
  /** この行の種類 (荒らし復旧 UI 第 1 段階)。`null` = 不明。 */
  readonly kind: PastCanvasKind | null;
  /** `kind === 'excluded'` の行だけ非 null (古いサーバが返さないときは欠落 = 無し扱い)。 */
  readonly exclusion?: PastCanvasExclusionInfo | null;
}

export type FetchPastCanvasListOutcome =
  | { readonly outcome: 'ok'; readonly items: readonly PastCanvasListItem[] }
  /** 401/403: 未オーナー (= 非オーナーが叩いた / token 失効)。 */
  | { readonly outcome: 'unauthorized' }
  /** 404/400: 部屋が無い / 削除済 / roomId 不正。 */
  | { readonly outcome: 'not-found' }
  /** network / 予期しない status / JSON 破損。 */
  | { readonly outcome: 'error' };

export interface FetchPastCanvasListInput {
  /** dispatcher base URL (`readEnv().dispatcherUrl`). 末尾 `/` は許容。 */
  readonly dispatcherUrl: string;
  readonly roomId: string;
  /** owner の admin_token (Bearer)。無指定は無認証で撃つ → 通常 401 → unauthorized。 */
  readonly adminToken?: string;
  readonly signal?: AbortSignal;
}

export interface PastCanvasClientDeps {
  readonly fetchFn?: FetchFn;
}

const authInit = (
  adminToken: string | undefined,
  signal: AbortSignal | undefined,
): { signal?: AbortSignal; headers?: Record<string, string> } => {
  const init: { signal?: AbortSignal; headers?: Record<string, string> } = {};
  if (signal !== undefined) init.signal = signal;
  if (adminToken !== undefined && adminToken.length > 0) {
    init.headers = { Authorization: `Bearer ${adminToken}` };
  }
  return init;
};

const PAST_CANVAS_KINDS = new Set<string>(['cleared', 'restore-autosave', 'picked', 'excluded']);

/** `kind` フィールド (`PastCanvasKind | null`) の shape 検証。 */
export const isPastCanvasKindOrNull = (v: unknown): v is PastCanvasKind | null =>
  v === null || (typeof v === 'string' && PAST_CANVAS_KINDS.has(v));

const isPastCanvasListItem = (v: unknown): v is PastCanvasListItem => {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    typeof o.sessionId === 'string' &&
    typeof o.snapshotSeq === 'number' &&
    typeof o.clearedSeq === 'number' &&
    typeof o.createdAt === 'string' &&
    typeof o.clearedAt === 'string' &&
    typeof o.layerCount === 'number' &&
    isPastCanvasKindOrNull(o.kind)
  );
};

export const fetchPastCanvasList = async (
  input: FetchPastCanvasListInput,
  deps: PastCanvasClientDeps = {},
): Promise<FetchPastCanvasListOutcome> => {
  const fetchFn = deps.fetchFn ?? defaultFetchFn;
  const baseUrl = input.dispatcherUrl.replace(/\/+$/, '');
  const url = `${baseUrl}/rooms/${encodeURIComponent(input.roomId)}/past-canvases`;

  let res: Response;
  try {
    res = await fetchFn(url, authInit(input.adminToken, input.signal));
  } catch {
    return { outcome: 'error' };
  }
  if (res.status === 401 || res.status === 403) return { outcome: 'unauthorized' };
  if (res.status === 404 || res.status === 400) return { outcome: 'not-found' };
  if (res.status !== 200) return { outcome: 'error' };

  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    return { outcome: 'error' };
  }
  if (typeof raw !== 'object' || raw === null) return { outcome: 'error' };
  const list = (raw as { pastCanvases?: unknown }).pastCanvases;
  if (!Array.isArray(list) || !list.every(isPastCanvasListItem)) return { outcome: 'error' };
  return { outcome: 'ok', items: list };
};

// ── 削除 (DELETE /rooms/:roomId/past-canvases/:id) ──────────────────────────
//
// 過去キャンバス 1 件のソフト削除 (Slice 4, §4.8)。owner-gated = `Authorization:
// Bearer <admin_token>` 必須。索引行を消すだけで R2 実体は触らない (sweeper が
// orphan として回収)。
//   - 204: 削除成功。
//   - 404: 既に無い / 別 room / 不正 id。呼び出し側は「消えている」= 冪等成功と
//     同義に扱い、一覧から除去する。
//   - 401/403: 未オーナー / token 失効。
//   - それ以外 (400/5xx/network): error。

export type DeletePastCanvasOutcome =
  | { readonly outcome: 'ok' }
  /** 404: 既に無い (冪等)。呼び出し側は削除成功と同様に一覧から除く。 */
  | { readonly outcome: 'not-found' }
  /** 401/403: 未オーナー / token 失効。 */
  | { readonly outcome: 'unauthorized' }
  /** 400/5xx/network / 予期しない status。カードは残して失敗表示。 */
  | { readonly outcome: 'error' };

export interface DeletePastCanvasInput {
  readonly dispatcherUrl: string;
  readonly roomId: string;
  readonly pastCanvasId: string;
  readonly adminToken?: string;
  readonly signal?: AbortSignal;
}

export const deletePastCanvas = async (
  input: DeletePastCanvasInput,
  deps: PastCanvasClientDeps = {},
): Promise<DeletePastCanvasOutcome> => {
  const fetchFn = deps.fetchFn ?? defaultFetchFn;
  const baseUrl = input.dispatcherUrl.replace(/\/+$/, '');
  const url = `${baseUrl}/rooms/${encodeURIComponent(input.roomId)}/past-canvases/${encodeURIComponent(
    input.pastCanvasId,
  )}`;

  const init = { ...authInit(input.adminToken, input.signal), method: 'DELETE' };

  let res: Response;
  try {
    res = await fetchFn(url, init);
  } catch {
    return { outcome: 'error' };
  }
  if (res.status === 204 || res.status === 200) return { outcome: 'ok' };
  if (res.status === 404) return { outcome: 'not-found' };
  if (res.status === 401 || res.status === 403) return { outcome: 'unauthorized' };
  return { outcome: 'error' };
};

// ── サムネ署名 (GET /rooms/:roomId/past-canvases/:id/snapshot-urls) ─────────

export interface PastCanvasSnapshotUrls {
  readonly metaUrl: string;
  readonly layerUrls: ReadonlyArray<{ readonly id: number; readonly url: string }>;
  /** 全 url 共通の TTL 秒。期限切れ (403) 時の再取得判断に使う。 */
  readonly urlExpiresInSeconds: number;
}

export type FetchPastCanvasSnapshotUrlsOutcome =
  | { readonly outcome: 'ok'; readonly urls: PastCanvasSnapshotUrls }
  | { readonly outcome: 'unauthorized' }
  /**
   * 404: 過去キャンバス無し / snapshot 無し・purge 済・backfill 前 / endpoint 未 mount
   * (= `SNAPSHOT_PUBLISHER` が s3 でない dev 環境)。いずれもサムネはプレースホルダに倒す。
   */
  | { readonly outcome: 'not-found' }
  /** 503: 署名失敗 (一時的)。 */
  | { readonly outcome: 'unavailable' }
  | { readonly outcome: 'error' };

export interface FetchPastCanvasSnapshotUrlsInput {
  readonly dispatcherUrl: string;
  readonly roomId: string;
  readonly pastCanvasId: string;
  readonly adminToken?: string;
  readonly signal?: AbortSignal;
}

const isLayerUrl = (v: unknown): v is { id: number; url: string } => {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return typeof o.id === 'number' && typeof o.url === 'string';
};

export const fetchPastCanvasSnapshotUrls = async (
  input: FetchPastCanvasSnapshotUrlsInput,
  deps: PastCanvasClientDeps = {},
): Promise<FetchPastCanvasSnapshotUrlsOutcome> => {
  const fetchFn = deps.fetchFn ?? defaultFetchFn;
  const baseUrl = input.dispatcherUrl.replace(/\/+$/, '');
  const url = `${baseUrl}/rooms/${encodeURIComponent(input.roomId)}/past-canvases/${encodeURIComponent(
    input.pastCanvasId,
  )}/snapshot-urls`;

  let res: Response;
  try {
    res = await fetchFn(url, authInit(input.adminToken, input.signal));
  } catch {
    return { outcome: 'error' };
  }
  if (res.status === 401 || res.status === 403) return { outcome: 'unauthorized' };
  if (res.status === 404 || res.status === 400) return { outcome: 'not-found' };
  if (res.status === 503) return { outcome: 'unavailable' };
  if (res.status !== 200) return { outcome: 'error' };

  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    return { outcome: 'error' };
  }
  if (typeof raw !== 'object' || raw === null) return { outcome: 'error' };
  const o = raw as Record<string, unknown>;
  if (
    typeof o.metaUrl !== 'string' ||
    !Array.isArray(o.layerUrls) ||
    !o.layerUrls.every(isLayerUrl) ||
    typeof o.urlExpiresInSeconds !== 'number'
  ) {
    return { outcome: 'error' };
  }
  return {
    outcome: 'ok',
    urls: {
      metaUrl: o.metaUrl,
      layerUrls: o.layerUrls,
      urlExpiresInSeconds: o.urlExpiresInSeconds,
    },
  };
};
